import React from "react";

import { SUPPORTED_CARRIERS, SUPPORTED_LIFE_CARRIERS, SUPPORTED_DISABILITY_CARRIERS } from "BrokerOfRecord/constants";
import { parseDigitsOnly } from "BrokerOfRecord/utils";
import { STATES_INVERTED } from "Constants";
import {
  validateRequired,
  validateZipCode,
  validatePhone,
  validateEmail,
  validateZero,
  validateHundred,
  validateNumber,
  validateName,
} from "Utils/Validations";

import { SIFormField, SIFormSelectWithSearch, SIFormSelect, SIFormTextArea } from "Common/forms";
import { zipCodeMask, phoneMask } from "Common/forms/masks";

export const produceGroupIdGuidance = ({ provider, stateCode }) => {
  let guidance;
  const defaultGuidance = "This is the ID the carrier uses to identify your group. ";
  const bcbsDefaultGuidance =
    'This is the ID the carrier uses to identify your group. You can find it on the first page of invoice, in top right corner next to "Profile Number:"';
  switch (provider) {
    case "united_healthcare":
      guidance = 'This is the ID the carrier uses to identify your group. You can find it on your invoice under "Customer Number".';
      break;
    case "kaiser":
      guidance = defaultGuidance;
      break;
    case "blue_shield":
      if (stateCode === "CA") {
        guidance = 'This is the ID the carrier uses to identify your group. You can find it on the first page of an invoice, next to "Account Number."';
      } else {
        guidance = defaultGuidance;
      }
      break;
    case "humana":
      guidance =
        'This is the ID the carrier uses to identify your group. You can find it on the first page of your invoice under "billing ID". Your ID is the first 6 digits.';
      break;
    case "bcbs":
      if (stateCode == "AZ") {
        guidance =
          "This is the ID the carrier uses to identify your group. You can find it on the first page of your renewal packet, on the top left side under company name.";
      } else if (!["NY", "MA"].includes(stateCode)) {
        guidance = ["TX", "MT", "IL", "OK", "NM"].includes(stateCode) ? bcbsDefaultGuidance : defaultGuidance;
      } else {
        guidance = bcbsDefaultGuidance;
      }
      break;
    case "cal_choice":
      guidance = defaultGuidance;
      break;
    case "premera_blue_cross":
      guidance =
        'This is the ID the carrier uses to identify your group. You can find it on the first page of welcome letter, left side next to "Group Number(s):"';
      break;
    case "anthem":
      guidance =
        'This is the ID the carrier uses to identify your group. You can find it on the 5th page of your renewal packet, on the left side next to "Group #."';
      break;
    case "aetna": {
      guidance =
        'This is the ID the carrier uses to identify your group. You can find it on the first page of invoice on the left side next to "Account Number/Bill Package."';
      break;
    }
    default:
      guidance = defaultGuidance;
      break;
  }

  return guidance;
};

export const PlanName = ({ ...rest }) => <SIFormField label="Plan Name" name="borPlanName" validate={validateRequired} {...rest} />;

// Medical
export const MedicalInsuranceProvider = ({ ...rest }) => (
  <SIFormField
    label="Insurance Provider"
    name="borMedicalInsuranceCompany"
    validate={validateRequired}
    component={SIFormSelectWithSearch}
    props={{ options: SUPPORTED_CARRIERS }}
    {...rest}
  />
);
export const MedicalGroupIdNumber = ({ provider, stateCode, ...rest }) => {
  const guidance = produceGroupIdGuidance({ provider, stateCode });
  return <SIFormField label="Group ID Number" name="borMedicalGroupIDNumber" validate={validateRequired} guidance={guidance} {...rest} />;
};
export const OtherMedicalInsuranceProvider = ({ ...rest }) => (
  <SIFormField label="Other Medical Insurance Provider" name="borOtherMedicalInsuranceCompany" validate={validateRequired} {...rest} />
);

// Dental
export const DentalInsuranceProvider = ({ ...rest }) => (
  <SIFormField
    label="Insurance Provider"
    name="borDentalInsuranceCompany"
    component={SIFormSelectWithSearch}
    props={{ options: SUPPORTED_CARRIERS }}
    {...rest}
  />
);
export const DentalGroupIdNumber = ({ ...rest }) => <SIFormField label="Group ID Number" name="borDentalGroupIDNumber" {...rest} />;
export const OtherDentalInsuranceProvider = ({ ...rest }) => (
  <SIFormField label="Other Dental Insurance Provider" name="borOtherDentalInsuranceCompany" validate={validateRequired} {...rest} />
);

export const VisionInsuranceProvider = ({ ...rest }) => (
  <SIFormField
    label="Insurance Provider"
    name="borVisionInsuranceCompany"
    component={SIFormSelectWithSearch}
    props={{ options: SUPPORTED_CARRIERS }}
    {...rest}
  />
);
export const VisionGroupIdNumber = ({ ...rest }) => <SIFormField label="Group ID Number" name="borVisionGroupIDNumber" {...rest} />;
export const OtherVisionInsuranceProvider = ({ ...rest }) => (
  <SIFormField label="Other Vision Insurance Provider" name="borOtherVisionInsuranceCompany" validate={validateRequired} {...rest} />
);

export const LifeInsuranceProvider = ({ ...rest }) => (
  <SIFormField
    label="Insurance Provider"
    name="borLifeInsuranceCompany"
    component={SIFormSelectWithSearch}
    props={{ options: SUPPORTED_LIFE_CARRIERS }}
    {...rest}
  />
);
export const LifeGroupIdNumber = ({ ...rest }) => <SIFormField label="Group ID Number" name="borLifeGroupIDNumber" {...rest} />;
export const OtherLifeInsuranceProvider = ({ ...rest }) => (
  <SIFormField label="Other Life Insurance Provider" name="borOtherLifeInsuranceCompany" validate={validateRequired} {...rest} />
);

// Disability
export const DisabilityInsuranceProvider = ({ ...rest }) => (
  <SIFormField
    label="Insurance Provider"
    name="borDisabilityInsuranceCompany"
    component={SIFormSelectWithSearch}
    props={{ options: SUPPORTED_DISABILITY_CARRIERS }}
    {...rest}
  />
);
export const DisabilityGroupIdNumber = ({ ...rest }) => <SIFormField label="Group ID Number" name="borDisabilityGroupIDNumber" {...rest} />;
export const OtherDisabilityInsuranceProvider = ({ ...rest }) => (
  <SIFormField label="Other Disability Insurance Provider" name="borOtherDisabilityInsuranceCompany" validate={validateRequired} {...rest} />
);

// Company Information
export const CompanyName = ({ ...rest }) => <SIFormField label="Company Name" name="name" validate={validateRequired} {...rest} />;
export const ContactName = ({ ...rest }) => (
  <SIFormField label="Authorized Representative Name" name="contactName" validate={[validateRequired, validateName]} {...rest} />
);
export const ContactTitle = ({ ...rest }) => <SIFormField label="Authorized Representative Title" name="contactTitle" validate={validateRequired} {...rest} />;
export const Email = ({ ...rest }) => (
  <SIFormField label="Authorized Representative Email" name="email" validate={[validateRequired, validateEmail]} {...rest} />
);
export const Address1 = ({ ...rest }) => <SIFormField label="Company Address" name="address1" validate={validateRequired} {...rest} />;
export const City = ({ ...rest }: any) => <SIFormField name="city" label="Company City" type="text" validate={validateRequired} {...rest} />;
export const State = ({ ...rest }: any) => (
  <SIFormField
    label="Company State"
    name="state"
    component={SIFormSelect}
    placeholder="-----"
    props={{ options: STATES_INVERTED }}
    validate={validateRequired}
    {...rest}
  />
);
export const Zip = ({ ...rest }) => (
  <SIFormField label="Company Zip Code" name="zip" props={{ mask: zipCodeMask }} validate={[validateRequired, validateZipCode]} {...rest} />
);
export const PhoneNumber = ({ ...rest }) => (
  <SIFormField
    label="Authorized Representative Phone Number"
    name="contactPhoneNumber"
    props={{ mask: phoneMask }}
    validate={[validateRequired, validatePhone]}
    {...rest}
  />
);

export const PartnerPrimaryPhoneNumber = ({ ...rest }: any) => (
  <SIFormField
    name="partnerPrimaryPhoneNumber"
    label="Primary Contact Phone Number (Optional)"
    guidance="If you would like to be contacted on a different phone number than the one synced from your payroll account, you can provide one here."
    type="text"
    props={{ placeholder: rest.placeholder, mask: phoneMask }}
    validate={[validatePhone]}
    {...rest}
  />
);

export const PartnerPrimaryEmail = ({ ...rest }: any) => (
  <SIFormField
    name="partnerPrimaryEmail"
    label="Primary Contact Email (Optional)"
    guidance="If you would like to be contacted on a different email than the one synced from your payroll account, you can provide one here."
    type="email"
    props={{ placeholder: rest.placeholder }}
    validate={[validateEmail]}
    {...rest}
  />
);

export const Password = ({ ...rest }) => <SIFormField label="Password" name="password" validate={validateRequired} props={{ type: "password" }} {...rest} />;

// Contribution Amounts
export const EmployeePercentage = ({ namePrefix, ...rest }: any) => (
  <SIFormField
    name={`${namePrefix}.employeePercentage`}
    label="Percentage for Employees"
    props={{
      appendContent: "%",
      type: "number",
      min: 0,
      max: 100,
    }}
    parse={parseDigitsOnly}
    guidance={["Typically ", <strong key="employee-percentage">{">50%"}</strong>, " of the employee's monthly cost."]}
    validate={[validateRequired, validateZero, validateHundred, validateNumber]}
    {...rest}
  />
);

export const DependentPercentage = ({ namePrefix, ...rest }: any) => (
  <SIFormField
    name={`${namePrefix}.dependentPercentage`}
    label="Percentage for Dependents"
    props={{
      appendContent: "%",
      type: "number",
      min: 0,
      max: 100,
    }}
    parse={parseDigitsOnly}
    guidance={["Typically ", <strong key="dependent-percentage">0%</strong>, " of the dependent cost. Many companies do not reimburse for dependents."]}
    validate={[validateRequired, validateZero, validateHundred, validateNumber]}
    {...rest}
  />
);

export const EmployeeFixedAmount = ({ namePrefix, ...rest }: any) => (
  <SIFormField
    name={`${namePrefix}.employeeFixedAmount`}
    label="Amount for Employees"
    props={{
      prependContent: "$",
      type: "number",
      min: 0,
    }}
    parse={parseDigitsOnly}
    guidance={["Typically ", <strong key="employee-amount">half or more</strong>, " of the employee's monthly cost."]}
    validate={[validateRequired, validateZero, validateNumber]}
    {...rest}
  />
);

export const DependentFixedAmount = ({ namePrefix, ...rest }: any) => (
  <SIFormField
    name={`${namePrefix}.dependentFixedAmount`}
    label="Amount for Dependents"
    props={{
      prependContent: "$",
      type: "number",
      min: 0,
    }}
    parse={parseDigitsOnly}
    guidance={["Typically ", <strong key="dependent-amount">zero</strong>, ". Many companies do not reimburse for dependents."]}
    validate={[validateRequired, validateZero, validateNumber]}
    {...rest}
  />
);

export const OtherContributionInformation = ({ ...rest }) => (
  <SIFormField
    name="otherContributionInformation"
    label="Additional Information on Company Benefit Contributions"
    component={SIFormTextArea}
    props={{
      placeholder:
        "Please include any additional details about custom insurance contributions, unique company contribution rules, or any other relevant information we should be aware of.",
    }}
  />
);
